import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { catchError, defaultIfEmpty, map, Observable, of, switchMap, take, tap } from 'rxjs';

import { AuthGuard, AuthService, LoginRedirectService, TenantService, UserService } from '@celum/authentication';
import { isTruthy } from '@celum/core';
import { PortalAccessRequestService } from '@celum/portals/api';
import { PortalConfiguration } from '@celum/portals/domain';
import { ConfigurationResolver, QueryParamTokensProviderService } from '@celum/portals/shared';

const NO_ACCESS = 'no_access';

@Injectable()
export class PortalsAuthGuard extends AuthGuard {
  private portalConfigResolver = inject(ConfigurationResolver);
  private tenantService = inject(TenantService);
  private userService = inject(UserService);
  private queryParamTokensProviderService = inject(QueryParamTokensProviderService, { optional: true });
  private location = inject(Location);
  private accessRequestService = inject(PortalAccessRequestService);

  constructor(authService: AuthService, router: Router, loginRedirectService: LoginRedirectService) {
    super(authService, router, loginRedirectService);
  }

  public override canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    this.queryParamTokensProviderService?.collectTokens(route);

    // Try to load the portal configuration. Protected portals will throw a 403 and return the tenantId, which is a signal to start authentication
    return this.portalConfigResolver.configuration$.pipe(
      take(1),
      switchMap(config => (!config ? this.portalConfigResolver.resolve(route) : of(config))),
      tap(config => this.tenantService.storeTenant(config.organizationId)),
      switchMap(config => this.refreshB2CTokenSilently().pipe(map(() => config))),
      // make sure to preload the user on protected portals so all other places have guaranteed access to the user from the start (initiated by portal app)
      switchMap(config => this.waitForUserOnProtectedPortalsIfNecessary(config)),
      catchError(error => {
        const queryParamTokens = this.queryParamTokensProviderService?.getTokens();
        // In case authentication happens via queryParamTokens ( === work opens portal via iframe ) and it fails,
        // we don't want to retry authentication, we just want to stop any further action
        if (error?.status === 403 && queryParamTokens) {
          return of(false);
        }

        // there is special handling for 403 errors with provided orgId below.
        // in all other error cases, we just want to prevent navigation
        const authErrorWithOrgId = error?.status === 403 && error?.error?.orgId;
        if (!authErrorWithOrgId) {
          return of(false);
        }

        /**
         * Getting to here means the user has tried to access a protected portal (orgId given), but is either not
         * logged in or doesn't have access to it.
         *
         * From here, the user is either
         * - redirected to login (if not logged in yet) OR
         * - redirected to "request access" (in case they are logged in, but have no access to the requested portal) OR
         * - redirected to "not-found" (via the same "path" as initial login redirect)
         */

        // This specific error contains the tenantId - store if for this session so the followup requests can be authenticated
        this.tenantService.storeTenant(error.error.orgId);

        // if user is already logged in, but has no access to the portal (not in org, not in audience), redirect to
        // request access. otherwise just continue the flow (will trigger login flow or redirect to not-found)
        if (error.error.code === NO_ACCESS) {
          this.redirectToRequestAccess(route);
          return of(false);
        }

        // Call the canActivate method to make sure we are logged in/handling the logged-in route
        return super.canActivate(route, state).pipe(
          // Handle one extra case: When a logged-in user visits a different protected portal for the first time, we need to reload after setting the tenantId
          tap(() => this.router.navigateByUrl(state.url))
        );
      }),
      map(canActivate => !!canActivate),
      tap(canActivate => canActivate && this.clearStoredAccessRequest(route))
    );
  }

  private refreshB2CTokenSilently(): Observable<void> {
    return this.authService.getMsalAccount() && !this.authService.isB2CTokenStillValid()
      ? this.authService.getB2CToken(true).pipe(
          map(() => {
            /* empty */
          }),
          defaultIfEmpty(void 0)
        )
      : of(void 0);
  }

  private redirectToRequestAccess(route: ActivatedRouteSnapshot): void {
    const portalSlug = route.paramMap.get('portalSlug') ?? '';

    // replace the current url, necessary in case the user has just logged in.
    // guards prevent the redirect to the portal url, and we would still show "logged-in"
    this.location.replaceState(`/${portalSlug}`);

    // navigate to the request-access page without location change
    // we still want to show the portal slug/id in the browser url!
    this.router.navigate(['/request-access', portalSlug], {
      skipLocationChange: true,
      state: { fromApp: true, portalSlug }
    });
  }

  /** Once the user is inside the portal, a previously sent access request is obsolete */
  private clearStoredAccessRequest(route: ActivatedRouteSnapshot): void {
    const userId = this.userService.userId();
    const slug = route?.paramMap.get('portalSlug');
    userId && slug && this.accessRequestService.clearRequested(slug, userId);
  }

  private waitForUserOnProtectedPortalsIfNecessary(configuration: PortalConfiguration): Observable<boolean> {
    // we don't load users on public portals if no valid b2c token is available
    if (!configuration.protected && !this.authService.isB2CTokenStillValid()) {
      return of(true);
    }

    // if authentication is done via query param tokens (= work-portal-review), there is no user to be loaded
    const isQueryParamTokenPortal = Boolean(this.queryParamTokensProviderService?.getTokens());
    if (isQueryParamTokenPortal) {
      return of(true);
    }

    return this.userService.currentUser$.pipe(
      isTruthy(),
      take(1),
      map(() => true)
    );
  }
}
